import React, { useState, useEffect, useRef } from 'react';
import { useApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, 
  DollarSign, 
  Calendar,
  Filter,
  Download,
  Eye,
  Search,
  RefreshCw,
  Plus,
  FileText,
  BarChart3
} from 'lucide-react';
import './Reporting.css';
import FinancialSummary from './FinancialSummary';

const accent = '#6366f1';
const accentLight = '#f1f5ff';

const Reporting = () => {
  const apiService = useApiService();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [filters, setFilters] = useState({
    customerId: '',
    status: '',
    startDate: '',
    endDate: '',
    reportType: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('payments');
  const [showroomDetails, setShowroomDetails] = useState(null);
  const financialSummaryRef = useRef(null);

  useEffect(() => {
    loadCustomers();
    loadReportData();
    loadUserInfo();
  }, [filters.customerId, filters.startDate, filters.endDate, filters.reportType]);

  useEffect(() => {
    loadShowroomDetails();
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadShowroomDetails = async () => {
    try {
      const data = await apiService.getUserShowroom();
      setShowroomDetails(data || {});
    } catch (error) {
      console.error('Error loading showroom details:', error);
    }
  };

  const loadUserInfo = async () => {
    try {
      // Get the current logged-in user from the database using /me endpoint
      const userData = await apiService.getCurrentUser();
      console.log('Current user data loaded from database:', userData);
      setUserInfo(userData);
    } catch (error) {
      console.error('Error loading current user info:', error);
      // Set default user info if API call fails
      setUserInfo({
        name: currentUser?.name || 'User',
        email: currentUser?.email || 'user@example.com',
        companyName: 'Company Name'
      });
    }
  };

  const loadCustomers = async () => {
    try {
      console.log('Loading customers...');
      const response = await apiService.getAllCustomers();
      console.log('Customer API response:', response);
      // Handle both response formats
      const customersData = response.customers || response.data || response || [];
      console.log('Processed customers data:', customersData);
      setCustomers(customersData);
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomers([]);
    }
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.customerId) params.customerId = filters.customerId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.reportType) params.reportType = filters.reportType;

      const response = await apiService.getReportingData(params);
      setReportData(response);
      setError(null);
    } catch (error) {
      console.error('Error loading report data:', error);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    if (!reportData || !reportData.customerPaymentSummaries) return [];
    
    let filtered = reportData.customerPaymentSummaries;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(summary => 
        summary.customer.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        summary.customer.cnic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        summary.customer.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(summary => {
        const netPosition = summary.netPosition || 0;
        if (filters.status === 'to_receive') return netPosition > 0;
        if (filters.status === 'to_give') return netPosition < 0;
        return true;
      });
    }

    return filtered;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'Rs. 0';
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-PK').format(number || 0);
  };

  const getProfitColor = (amount) => {
    if (!amount || amount === 0) return '#6b7280';
    return amount > 0 ? '#10b981' : '#ef4444';
  };

  const calculateTotals = () => {
    const filteredData = getFilteredData();
    return filteredData.reduce((acc, summary) => {
      acc.totalToReceive += summary.amountsToReceive || 0;
      acc.totalToGive += summary.amountsToGive || 0;
      return acc;
    }, { totalToReceive: 0, totalToGive: 0 });
  };

  const totals = calculateTotals();

  // Export functionality - dynamic based on active tab
  const exportToCSV = () => {
    // If Financial Summary tab is active, use its export function
    if (activeTab === 'financial' && financialSummaryRef.current) {
      financialSummaryRef.current.exportToCSV();
      return;
    }

    // Otherwise, export Customer Payments data
    const filteredData = getFilteredData();
    
    if (filteredData.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV headers
    const headers = [
      'Customer Name',
      'CNIC',
      'Client Type',
      'To Receive',
      'To Give',
      'Status'
    ];

    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...filteredData.map(summary => [
        `"${summary.customer.fullName}"`,
        `"${summary.customer.cnic}"`,
        `"${summary.customer.clientType}"`,
        summary.amountsToReceive || 0,
        summary.amountsToGive || 0,
        `"${summary.netPosition >= 0 ? 'TO RECEIVE' : 'TO GIVE'}"`
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `customer_payments_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const ensureShowroomDetails = async () => {
    if (showroomDetails) return showroomDetails;
    try {
      const data = await apiService.getUserShowroom();
      setShowroomDetails(data || {});
      return data || {};
    } catch (error) {
      console.error('Error loading showroom details:', error);
      return {};
    }
  };

  const buildShowroomHeader = (details = {}, fallbackName) => {
    const name = details.showroomName || details.name || fallbackName || 'Company Name';
    const contact = details.showroomPhone || details.contact || '';
    const email = details.showroomEmail || details.email || userInfo?.email || currentUser?.email || 'user@example.com';
    const owner = details.showroomOwner || details.ownerName || userInfo?.name || currentUser?.name || 'User';
    const address = details.showroomAddress || details.address || '';

    return `
      <div style="text-align: center; margin-bottom: 6px;">
        <div style="font-size: 24px; font-weight: bold; margin-bottom: 5px;">${name}</div>
        <div style="font-size: 12px; color: #666; margin-bottom: 4px;">Generated on: ${new Date().toLocaleString('en-PK')}</div>
        <div style="font-size: 12px; color: #666; margin-bottom: 4px;">Showroom Owner: ${owner}</div>
        <div style="font-size: 12px; color: #666; margin-bottom: 4px;">${email}</div>
        ${contact ? `<div style="font-size: 12px; color: #666; margin-bottom: 4px;">${contact}</div>` : ''}
        ${address ? `<div style="font-size: 12px; color: #666;">${address}</div>` : ''}
      </div>
    `;
  };

  // PDF Export functionality - dynamic based on active tab
  const exportToPDF = async () => {
    // If Financial Summary tab is active, use its export function
    if (activeTab === 'financial' && financialSummaryRef.current) {
      await financialSummaryRef.current.exportToPDF();
      return;
    }

    // Otherwise, export Customer Payments data
    const filteredData = getFilteredData();
    
    if (filteredData.length === 0) {
      alert('No data to export');
      return;
    }

    try {
      const headerShowroom = await ensureShowroomDetails();
      // Dynamic import for jsPDF and html2canvas
      const { default: jsPDF } = await import('jspdf');
      const html2canvas = await import('html2canvas');

      // Create a temporary div for the PDF content
      const pdfContent = document.createElement('div');
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.top = '0';
      pdfContent.style.width = '1200px'; // Increased width for landscape mode
      pdfContent.style.padding = '20px';
      pdfContent.style.fontFamily = 'Arial, sans-serif';
      pdfContent.style.backgroundColor = 'white';
      pdfContent.style.color = 'black';

      // Get user information
      const userName = userInfo?.name || currentUser?.name || 'User';
      const userEmail = userInfo?.email || currentUser?.email || 'user@example.com';
      const companyName = headerShowroom?.showroomName || headerShowroom?.name || userInfo?.companyName || 'Company Name';

      pdfContent.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
          ${buildShowroomHeader(headerShowroom, companyName)}
          <div style="font-size: 20px; margin-top: 8px; margin-bottom: 10px;">Customer Payment Report</div>
        </div>
        
        <div style="margin-bottom: 20px;">
          <h3 style="margin-top: 0;">Summary</h3>
          <div style="margin-bottom: 10px;"><strong>Total To Receive:</strong> ${formatCurrency(totals.totalToReceive)}</div>
          <div style="margin-bottom: 10px;"><strong>Total To Give:</strong> ${formatCurrency(totals.totalToGive)}</div>
          <div style="margin-bottom: 10px;"><strong>Total Customers:</strong> ${filteredData.length}</div>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background-color: #f2f2f2;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Customer Name</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">CNIC</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Client Type</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">To Receive</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">To Give</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData.map(summary => `
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${summary.customer.fullName}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${summary.customer.cnic || 'N/A'}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${summary.customer.clientType}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${formatCurrency(summary.amountsToReceive)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${formatCurrency(summary.amountsToGive)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${summary.netPosition >= 0 ? 'TO RECEIVE' : 'TO GIVE'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;

      document.body.appendChild(pdfContent);

      // Convert to canvas
      const canvas = await html2canvas.default(pdfContent, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Remove the temporary element
      document.body.removeChild(pdfContent);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); // Changed to landscape mode
      const imgWidth = 297; // A4 landscape width
      const pageHeight = 210; // A4 landscape height
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Add footer to all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text('powered by ledger Glide | +923332416545', imgWidth - 10, pageHeight - 5, { align: 'right' });
      }

      // Save the PDF
      pdf.save(`customer_payments_${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#6b7280' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTop: `4px solid ${accent}`, borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '1rem' }}></div>
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#dc2626' }}>
          <p>{error}</p>
          <button onClick={loadReportData} style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '500', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Debug: Show if no data
  if (!reportData) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#dc2626' }}>
          <p>No report data available</p>
          <button onClick={loadReportData} style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '500', cursor: 'pointer' }}>
            Load Data
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em' }}>Financial Reports</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Customer payment summaries and financial positions</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem' }}>
            <Filter size={16} />
            <span className="d-none d-sm-inline">Filter</span>
          </button>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }} onClick={exportToCSV}>
            <Download size={16} />
            <span className="d-none d-sm-inline">Export CSV</span>
          </button>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(220, 38, 38, 0.1), 0 1px 2px 0 rgba(220, 38, 38, 0.06)' }} onClick={exportToPDF}>
            <FileText size={16} />
            <span className="d-none d-sm-inline">Export PDF</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          background: 'white',
          padding: '0.5rem',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f3f4f6'
        }}>
          <button
            onClick={() => setActiveTab('payments')}
            style={{ 
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              background: activeTab === 'payments' ? accent : '#f8fafc',
              color: activeTab === 'payments' ? '#ffffff' : '#64748b',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <DollarSign size={16} />
              Customer Payments
            </div>
          </button>
          <button
            onClick={() => setActiveTab('financial')}
            style={{ 
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              background: activeTab === 'financial' ? accent : '#f8fafc',
              color: activeTab === 'financial' ? '#ffffff' : '#64748b',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BarChart3 size={16} />
              Financial Summary
            </div>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'payments' ? (
        <>
          {/* Stats Cards */}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DollarSign size={20} style={{ color: '#0284c7' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total To Receive</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(totals.totalToReceive)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef2f2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DollarSign size={20} style={{ color: '#dc2626' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total To Give</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(totals.totalToGive)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: accentLight, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Users size={20} style={{ color: accent }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Customers</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatNumber(reportData?.customerPaymentSummaries?.length || 0)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters Section - Combined in one row */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
        <div className="card-body p-3 p-md-4">
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search */}
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search customers by name or CNIC..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', paddingLeft: '16px', fontSize: '0.875rem' }}
                />
              </div>
            </div>

            {/* Customer Filter */}
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <select
                value={filters.customerId}
                onChange={(e) => handleFilterChange('customerId', e.target.value)}
                className="form-control"
                style={{ padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.875rem' }}
              >
                <option value="">All Customers</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.fullName} ({customer.clientType})
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div style={{ flex: '1 1 150px', minWidth: 0 }}>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="form-control"
                style={{ padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.875rem' }}
              >
                <option value="">All Status</option>
                <option value="to_receive">To Receive</option>
                <option value="to_give">To Give</option>
              </select>
            </div>

            {/* Clear Filters Button */}
            <div style={{ minWidth: 140, textAlign: 'right', flex: 'none' }}>
              <button
                onClick={() => {
                  setFilters({
                    customerId: '',
                    status: '',
                    startDate: '',
                    endDate: '',
                    reportType: 'all'
                  });
                  setSearchTerm('');
                }}
                style={{ 
                  padding: '0.75rem 1rem', 
                  background: '#f8fafc', 
                  color: '#64748b', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '8px', 
                  fontSize: '0.875rem', 
                  fontWeight: '500',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Payments Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading report data...</div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Customer</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>To Receive</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>To Give</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredData().map(summary => (
                <tr key={summary.customer.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{summary.customer.fullName}</div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '0.5rem' }}>{summary.customer.cnic}</div>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px', 
                        fontSize: '0.75rem', 
                        fontWeight: '500',
                        background: '#e0e7ff', 
                        color: '#3730a3' 
                      }}>{summary.customer.clientType}</span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {formatCurrency(summary.amountsToReceive)}
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {formatCurrency(summary.amountsToGive)}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ 
                      padding: '0.25rem 0.75rem', 
                      borderRadius: '20px', 
                      fontSize: '0.75rem', 
                      fontWeight: '600', 
                      textTransform: 'uppercase',
                      background: summary.netPosition >= 0 ? '#d1fae5' : '#fef3c7',
                      color: summary.netPosition >= 0 ? '#065f46' : '#92400e'
                    }}>
                      {summary.netPosition >= 0 ? 'TO RECEIVE' : 'TO GIVE'}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          width: '32px', 
                          height: '32px', 
                          border: 'none', 
                          borderRadius: '6px', 
                          cursor: 'pointer', 
                          background: '#f3f4f6', 
                          color: '#6b7280' 
                        }}
                        onClick={() => {
                          // Navigate to customer detail page without opening new tab
                          window.location.href = `/reporting/customer/${summary.customer.id}`;
                        }}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
        </>
      ) : (
        <FinancialSummary ref={financialSummaryRef} />
      )}
    </div>
  );
};

export default Reporting; 